<template>
  <!-- The Retainers list under Finance as the app draws it today (Workspace369 origin/main, Oct 22 2026).
       Sources: lib/view/retainers/retainers_list_screen.dart (toolbar: search, Export, New retainer;
       AppUnderlineTabs All retainers / Hour bank / Fixed monthly / Prepaid; view bar with the Next
       invoice sort chip, Filter, Columns; AppMetricStrip with its four metrics; AppDataTable columns
       and widths; phone: search, Active / Review / Ended segments, RetainerPhoneCard list),
       widgets/retainer_list_cells.dart (name + terms, client chip with initials tile, This cycle
       figure + note + meter, amount, next invoice, collection, standing tag), widgets/retainer_meter.dart
       (solid ink = approved, striped = pending, red tail = over, amber marker = top-up point),
       widgets/retainer_phone_card.dart (stripe, name + tag, three facts, meter + footer),
       retainer_labels.dart (standing words and tones), lib/model/invoice/retainer_facts.dart (80% used
       becomes "NN% used", past the bank becomes "Over by"), widgets/data_display/app_metric_strip.dart,
       and SidebarV2 for Finance > Retainers. Labels are app_en.arb strings; Northstar Studio, the
       clients and the amounts are sample data. The first retainer walks through the cycle: 6 of 30 h
       approved, 18 of 30 h, then 26.5 of 30 h with 5 h still pending (88% used). The server renders
       `pending`. Radius exception: the app draws the retainer meter as a rounded bar and the phone card
       at 14px; both are the app's own. Not verifiable from source: the phone's top title bar and which
       bottom tab the shell highlights for Retainers (it is not one of the default tabs). -->
  <div class="raw" :class="`raw--${state}`" aria-hidden="true">
    <svg class="raw-sprite" width="0" height="0">
      <symbol v-for="name in used" :id="`raw-${name}`" :key="name" viewBox="0 0 24 24"><path :d="icons[name]" /></symbol>
    </svg>

    <!-- Desktop and tablet -->
    <div class="raw-desk">
      <aside class="raw-side">
        <div class="raw-side-top"><svg class="ic"><use href="#raw-view_sidebar" /></svg></div>
        <div class="raw-ws"><span class="raw-logo">N</span><span class="raw-ell">Northstar Studio</span><svg class="ic sm"><use href="#raw-unfold_more" /></svg></div>
        <nav class="raw-nav">
          <template v-for="g in nav" :key="g.k">
            <div class="raw-ni top">
              <svg class="ic"><use :href="`#raw-${g.i}`" /></svg><span>{{ g.k }}</span>
              <svg v-if="g.kids || g.chev" class="ic sm raw-chev"><use :href="`#raw-${g.open ? 'expand_more' : 'chevron_right'}`" /></svg>
            </div>
            <template v-if="g.open">
              <div v-for="c in g.kids" :key="c" class="raw-ni child" :class="{ sel: c === 'Retainers' }"><span>{{ c }}</span></div>
            </template>
          </template>
        </nav>
        <div class="raw-side-foot">
          <div v-for="f in foot" :key="f.k" class="raw-ni"><svg class="ic"><use :href="`#raw-${f.i}`" /></svg><span>{{ f.k }}</span></div>
        </div>
      </aside>

      <div class="raw-main">
        <header class="raw-head">
          <span class="raw-title"><svg class="ic sm"><use href="#raw-handshake" /></svg>Retainers</span>
          <span class="raw-hsearch"><svg class="ic sm"><use href="#raw-search" /></svg>Search...<kbd>⌘K</kbd></span>
          <span class="raw-icbtn raw-bell"><svg class="ic"><use href="#raw-notifications" /></svg><b>3</b></span>
          <span class="raw-icbtn"><svg class="ic"><use href="#raw-settings" /></svg></span>
          <span class="raw-icbtn"><svg class="ic"><use href="#raw-chat_bubble_outline" /></svg></span>
        </header>

        <section class="raw-sheet">
          <div class="raw-toolbar">
            <span class="raw-search"><svg class="ic"><use href="#raw-search" /></svg>Search retainers or clients</span>
            <span class="raw-btn sec"><svg class="ic"><use href="#raw-download" /></svg>Export</span>
            <span class="raw-btn"><svg class="ic"><use href="#raw-add" /></svg>New retainer</span>
          </div>
          <div class="raw-tabs">
            <span class="on"><svg class="ic xs"><use href="#raw-handshake" /></svg>All retainers<i>6</i></span><span>Hour bank<i>3</i></span><span>Fixed monthly<i>2</i></span><span>Prepaid<i>1</i></span>
          </div>
          <div class="raw-filters">
            <span class="raw-chip"><svg class="ic xs"><use href="#raw-swap_vert" /></svg>Next invoice<svg class="ic xs"><use href="#raw-expand_more" /></svg></span>
            <span class="raw-chip"><svg class="ic xs"><use href="#raw-add" /></svg>Filter</span>
            <span class="raw-grow" />
            <span class="raw-chip"><svg class="ic xs"><use href="#raw-view_column" /></svg>Columns<svg class="ic xs"><use href="#raw-expand_more" /></svg></span>
          </div>
          <div class="raw-metrics">
            <div v-for="m in metrics" :key="m.k" class="raw-metric">
              <span class="raw-m-l"><svg class="ic xs"><use :href="`#raw-${m.i}`" /></svg>{{ m.k }}</span>
              <span class="raw-m-v">{{ m.v }}<small v-if="m.suffix">{{ m.suffix }}</small></span>
              <span class="raw-m-c" :class="{ bad: m.bad }">{{ m.c }}</span>
            </div>
          </div>
          <div class="raw-table">
            <table>
              <colgroup><col class="w-ret"><col class="w-cli c-cli"><col class="w-cyc"><col class="w-amt"><col class="w-next"><col class="w-col c-col"><col class="w-st"><col class="w-act"></colgroup>
              <thead>
                <tr>
                  <th v-for="c in columns" :key="c.k" :class="[{ r: c.r }, c.cls]"><svg v-if="c.i" class="ic xs"><use :href="`#raw-${c.i}`" /></svg>{{ c.k }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.name" :class="{ 'is-focus': row.focus }">
                  <td><span class="raw-two"><b>{{ row.name }}</b><small>{{ row.terms }}</small></span></td>
                  <td class="c-cli"><span class="raw-client"><i class="raw-init" :class="row.tone">{{ row.init }}</i><span class="raw-ell">{{ row.client }}</span></span></td>
                  <td>
                    <span class="raw-cycle">
                      <span class="raw-fig"><b :class="{ bad: row.over }">{{ row.figure }}</b><small v-if="row.note" :class="{ bad: row.over }">{{ row.note }}</small></span>
                      <span v-if="!row.noMeter" class="raw-meter" :class="{ money: row.money }">
                        <i class="m-used" :style="{ width: row.m.used + '%' }" />
                        <i v-if="row.m.over" class="m-over" :style="{ width: row.m.over + '%' }" />
                        <i v-if="row.m.pending" class="m-pend" :style="{ width: row.m.pending + '%' }" />
                        <u v-if="row.m.marker" :style="{ left: row.m.marker + '%' }" />
                      </span>
                    </span>
                  </td>
                  <td class="r"><span class="raw-two end"><b>{{ row.amount }}</b><small>{{ row.per }}</small></span></td>
                  <td><span class="raw-two"><b>{{ row.next }}</b><small v-if="row.nextNote" :class="{ warn: row.nextWarn }">{{ row.nextNote }}</small></span></td>
                  <td class="c-col"><span class="raw-coll"><svg class="ic xs"><use :href="`#raw-${row.card ? 'bolt' : 'link'}`" /></svg><em v-if="row.card" class="raw-mark">{{ row.card }}</em>{{ row.collection }}</span></td>
                  <td><em class="raw-tag" :class="row.tag">{{ row.status }}</em></td>
                  <td class="more"><svg class="ic"><use href="#raw-more_horiz" /></svg></td>
                </tr>
              </tbody>
            </table>
          </div>
          <footer class="raw-foot">
            <span>6 retainers</span><span class="raw-grow" />
            <span class="raw-chip"><svg class="ic xs"><use href="#raw-view_module" /></svg>20 rows<svg class="ic xs"><use href="#raw-expand_more" /></svg></span>
            <span class="raw-chip">Previous</span><span class="muted">1 / 1</span><span class="raw-chip">Next</span>
          </footer>
        </section>
      </div>
    </div>

    <!-- Phone -->
    <div class="raw-phone">
      <div class="rap-bar"><b>Retainers</b></div>
      <div class="rap-body">
        <span class="rap-search"><svg class="ic"><use href="#raw-search" /></svg>Search retainers or clients</span>
        <div class="rap-seg"><span class="on">Active<i>6</i></span><span>Review</span><span>Ended</span></div>
        <article v-for="card in cards" :key="card.name" class="rap-card" :class="{ 'is-focus': card.focus }" :style="{ '--stripe': `var(--${card.stripe}-t)` }">
          <div class="rap-in">
            <div class="rap-head"><b>{{ card.name }}</b><em class="raw-tag" :class="card.tag">{{ card.status }}</em></div>
            <p class="rap-client">{{ card.client }}</p>
            <dl class="rap-facts">
              <div v-for="f in card.facts" :key="f.l"><dt>{{ f.l }}</dt><dd>{{ f.v }}</dd></div>
            </dl>
            <div class="rap-foot">
              <span class="raw-meter" :class="{ money: card.money }">
                <i class="m-used" :style="{ width: card.m.used + '%' }" />
                <i v-if="card.m.over" class="m-over" :style="{ width: card.m.over + '%' }" />
                <i v-if="card.m.pending" class="m-pend" :style="{ width: card.m.pending + '%' }" />
                <u v-if="card.m.marker" :style="{ left: card.m.marker + '%' }" />
              </span>
              <span class="rap-note">{{ card.footer }}</span>
            </div>
          </div>
        </article>
      </div>
      <nav class="rap-nav">
        <span v-for="t in tabs" :key="t.k"><svg class="ic"><use :href="`#raw-${t.i}`" /></svg>{{ t.k }}</span>
        <span class="rap-navmore"><svg class="ic"><use href="#raw-add" /></svg></span>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { appIcons } from "~/data/app-icons.js";
import { retainerIcons } from "~/data/app-icons-retainers.js";

const props = defineProps({
  // early | mid | pending
  state: { type: String, default: "pending" },
});

const icons = { ...appIcons, ...retainerIcons };
const used = ["account_balance_wallet", "add", "attach_money", "auto_awesome", "bolt", "business", "calendar_today", "chat_bubble_outline", "chevron_right", "dashboard", "donut_large", "download", "attention", "event", "expand_more", "flag", "forum", "handshake", "handyman", "hourglass", "link", "logout", "more_horiz", "notifications", "people_alt", "receipt_long", "search", "settings", "support_agent", "swap_vert", "unfold_more", "view_column", "view_module", "view_sidebar", "work_outline", "workspaces"];

const nav = [
  { k: "Dashboard", i: "dashboard" },
  { k: "AI AutoPilot", i: "auto_awesome", chev: true },
  { k: "Clients", i: "people_alt", kids: ["Clients", "Requests"] },
  { k: "Work", i: "work_outline", kids: ["Projects", "Calendar", "Tasks", "Reminders", "Time Tracking"] },
  { k: "Finance", i: "account_balance_wallet", open: true, kids: ["Proposals", "Contracts", "Invoices", "Retainers", "Expenses", "Accounting", "Product Catalog"] },
  { k: "Communications", i: "forum", kids: ["Inbox", "Emails", "Mobile", "Campaigns", "Workflows", "Funnels"] },
  { k: "Tools", i: "handyman", kids: ["Notes", "Files", "Habits"] },
];
const foot = [
  { k: "Settings", i: "settings" },
  { k: "Support", i: "support_agent" },
  { k: "Log Out", i: "logout" },
];
const columns = [
  { k: "Retainer", i: "handshake" },
  { k: "Client", i: "business", cls: "c-cli" },
  { k: "This cycle", i: "donut_large" },
  { k: "Amount", i: "attach_money", r: true },
  { k: "Next invoice", i: "event" },
  { k: "Collection", i: "bolt", cls: "c-col" },
  { k: "Status", i: "flag" },
  { k: "Actions", cls: "more" },
];
const tabs = [
  { k: "Home", i: "dashboard" },
  { k: "Projects", i: "workspaces" },
  { k: "AI AutoPilot", i: "auto_awesome" },
  { k: "Invoices", i: "receipt_long" },
  { k: "Calendar", i: "calendar_today" },
];

// The 30 h bank that moves: approved and pending hours for each state.
const cycle = {
  early: { approved: 6, pending: 0 },
  mid: { approved: 18, pending: 0 },
  pending: { approved: 26.5, pending: 5 },
};
const fmt = (n) => String(n);
// Compact amounts as the card writes them (shorten()).
const money = (n) => "$" + n + "k";
const focus = computed(() => {
  const c = cycle[props.state] || cycle.pending;
  const total = Math.max(30, c.approved + c.pending);
  const pct = Math.floor((c.approved / 30) * 100 + 1e-9);
  const nearly = c.approved / 30 >= 0.8;
  return {
    ...c,
    figure: `${fmt(c.approved)} of 30 h`,
    note: c.pending ? `${fmt(c.pending)} h pending` : "",
    status: nearly ? `${pct}% used` : "Active",
    tag: nearly ? "amber" : "green",
    stripe: nearly ? "amber" : "green",
    footer: c.pending ? `${fmt(c.pending)} h pending` : `${fmt(30 - c.approved)} h left`,
    m: { used: (c.approved / total) * 100, pending: (c.pending / total) * 100 },
  };
});

const rows = computed(() => {
  const f = focus.value;
  return [
    { focus: true, name: "Growth marketing retainer", terms: "Hour bank · 30 h", client: "Marlowe Coffee Co.", init: "MC", tone: "red", figure: f.figure, note: f.note, m: f.m, amount: "$4,500.00", per: "per month", next: "Nov 1", card: "VISA", collection: "•• 4242", status: f.status, tag: f.tag },
    { name: "Brand care plan", terms: "Fixed monthly", client: "Juniper Bakery", init: "JB", tone: "red", figure: "Fixed scope", m: {}, noMeter: true, amount: "$1,800.00", per: "per month", next: "Nov 1", collection: "Pay link", status: "Active", tag: "green" },
    { name: "Design deposit", terms: "Prepaid deposit", client: "Atlas Logistics", init: "AL", tone: "blue", figure: "$3,100 left", note: "of $5,000", money: true, m: { used: 38, marker: 90 }, amount: "$5,000.00", per: "deposit", next: "At $500", nextNote: "$2,500 top-up", collection: "Pay link", status: "Active", tag: "green" },
    { name: "Website care plan", terms: "Fixed monthly", client: "Orchard Veterinary", init: "OV", tone: "orange", figure: "Fixed scope", m: {}, noMeter: true, amount: "$650.00", per: "per month", next: "Nov 8", card: "VISA", collection: "•• 1881", status: "Active", tag: "green" },
    { name: "Support hours", terms: "Hour bank · 20 h", client: "Harbor & Co.", init: "H", tone: "blue", figure: "23.5 of 20 h", note: "3.5 h over", over: true, m: { used: 85.1, over: 14.9 }, amount: "$2,700.00", per: "per month", next: "Nov 15", nextNote: "+ $525.00 extra", nextWarn: true, card: "MC", collection: "•• 8210", status: "Over by 3.5 h", tag: "red" },
    { name: "SEO retainer", terms: "Hour bank · 15 h", client: "Brightside Dental", init: "BD", tone: "amber", figure: "12.5 of 15 h", m: { used: 83.3 }, amount: "$2,250.00", per: "per month", next: "Nov 20", card: "VISA", collection: "•• 3305", status: "83% used", tag: "amber" },
  ];
});

const metrics = computed(() => {
  const c = focus.value;
  const usedHours = c.approved + 23.5 + 12.5;
  return [
    { k: "Retainer revenue", i: "handshake", v: "$11,900", suffix: "/mo", c: "5 billing" },
    { k: "Hours used this cycle", i: "hourglass", v: `${fmt(usedHours)} of 65 h`, c: c.pending ? `3 hour banks · ${fmt(c.pending)} h pending` : "3 hour banks" },
    { k: "Prepaid held", i: "account_balance_wallet", v: "$3,100", c: "Atlas Logistics · top up below $500" },
    { k: "Needs attention", i: "attention", v: "1", c: "Harbor & Co. over", bad: true },
  ];
});

// Phone cards: amounts as the card writes them (compact, "/mo"), hours as the cycle figure.
const cards = computed(() => {
  const f = focus.value;
  return [
    { focus: true, name: "Growth marketing retainer", client: "Marlowe Coffee Co.", status: f.status, tag: f.tag, stripe: f.stripe, m: f.m, footer: f.footer, facts: [{ l: "This cycle", v: f.figure }, { l: "Amount", v: `${money(4.5)}/mo` }, { l: "Next", v: "Nov 1" }] },
    { name: "Design deposit", client: "Atlas Logistics", status: "Active", tag: "green", stripe: "green", money: true, m: { used: 38, marker: 90 }, footer: `of ${money(5)}`, facts: [{ l: "Balance", v: money(3.1) }, { l: "Amount", v: money(5) }, { l: "Next", v: "—" }] },
    { name: "Support hours", client: "Harbor & Co.", status: "Over", tag: "red", stripe: "red", m: { used: 85.1, over: 14.9 }, footer: "3.5 h extra", facts: [{ l: "This cycle", v: "23.5 of 20 h" }, { l: "Amount", v: `${money(2.7)}/mo` }, { l: "Next", v: "Nov 15" }] },
  ];
});
</script>

<style>
/* Tokens: shadcn Neutral (the app's default scheme) blended by AppShellPalette; tags are AppTone pairs. */
.raw {
  --ink: #0a0a0a; --primary: #171717; --on-primary: #fafafa;
  --fill: #eef1f4; --canvas: #f7f7f7; --page: #fff; --edge: #ebebeb; --card-edge: #e6e6e6; --line2: #efefef;
  --ink2: #4f4f4f; --ink3: #717171; --quiet: #a7a7a7; --selected: rgb(10 10 10 / 0.06); --hover: rgb(10 10 10 / 0.04);
  --track: rgb(10 10 10 / 0.08); --meter: rgb(10 10 10 / 0.88);
  --phone-page: #f1f1f1; --phone-card: #fafafa; --phone-line: rgb(10 10 10 / 0.08);
  --gray-t: #5e5e5e; --gray-f: rgb(0 0 0 / 0.067); --green-t: #2d7a4e; --green-f: #e6f4ea; --blue-t: #3f58bf; --blue-f: #ebf0fd;
  --red-t: #ba3a37; --red-f: #fbeaea; --amber-t: #946000; --amber-f: #fdf4c3; --orange-t: #ad4e00; --orange-f: #fcedd6;
  --entry: #d4a00a;
  position: relative;
  container-type: inline-size;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  text-align: left;
}
.dark .raw {
  --ink: #fafafa; --primary: #fafafa; --on-primary: #171717;
  --fill: #232527; --canvas: #121212; --page: #1a1a1a; --edge: #272727; --card-edge: #2c2c2c; --line2: #252525;
  --ink2: #c2c2c2; --ink3: #9a9a9a; --quiet: #6b6b6b; --selected: rgb(250 250 250 / 0.1); --hover: rgb(250 250 250 / 0.05);
  --track: rgb(250 250 250 / 0.12); --meter: rgb(250 250 250 / 0.86);
  --phone-page: #141414; --phone-card: #242424; --phone-line: rgb(250 250 250 / 0.08);
  --gray-t: #bdbdbd; --gray-f: rgb(255 255 255 / 0.12); --green-t: #6ed39a; --green-f: rgb(63 178 110 / 0.18); --blue-t: #93aef9; --blue-f: rgb(91 127 234 / 0.2);
  --red-t: #f69591; --red-f: rgb(229 83 78 / 0.2); --amber-t: #f1c95e; --amber-f: rgb(224 170 36 / 0.17); --orange-t: #f6ac6b; --orange-f: rgb(233 131 45 / 0.18);
}
/* The window's size is reserved before its markup has streamed in (see InvoicesAppWindow): same sizes as the layouts below. */
.raw { min-height: 600px; }
@media (max-width: 1128px) { .raw { min-height: 560px; } }
/* 648 = the 600px phone container plus the hero's 24px gutters. */
@media (max-width: 648px) { .raw { min-height: 0; aspect-ratio: 390 / 700; } }
.raw-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
/* display: inline-block undoes Tailwind's preflight (svg { display: block }). */
.raw .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.raw .ic.sm { width: 16px; height: 16px; }
.raw .ic.xs { width: 14px; height: 14px; }
.raw-grow { flex: 1; }
.raw-ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.raw .muted { color: var(--ink2); }

/* ---------- desktop ---------- */
.raw-desk {
  display: grid;
  grid-template-columns: 252px minmax(0, 1fr);
  height: 600px;
  overflow: hidden;
  border: 1px solid var(--card-edge);
  border-radius: 10px;
  background: var(--canvas);
  box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12);
  -webkit-mask-image: linear-gradient(#000 92%, transparent);
  mask-image: linear-gradient(#000 92%, transparent);
}
.dark .raw-desk { border-color: #3a3a3a; box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.06), 0 30px 60px -30px rgb(0 0 0 / 0.7); }
.dark .raw-phone { border-color: #3a3a3a; }
.raw-side { display: flex; flex-direction: column; min-height: 0; padding: 0 10px 10px; }
.raw-side-top { display: flex; align-items: center; height: 48px; padding-left: 6px; color: var(--ink2); }
.raw-ws { display: flex; align-items: center; gap: 10px; height: 40px; margin-bottom: 6px; border-bottom: 1px solid var(--edge); padding: 0 6px 6px; font-weight: 600; }
.raw-ws .ic { margin-left: auto; color: var(--ink3); }
.raw-logo { display: grid; flex: none; place-items: center; width: 26px; height: 26px; border-radius: 6px; background: #171717; color: #fafafa; font-size: 12px; font-weight: 700; }
.dark .raw-logo { background: #fafafa; color: #171717; }
.raw-nav { flex: 1; min-height: 0; overflow: hidden; }
.raw-ni { display: flex; align-items: center; gap: 10px; height: 32px; padding: 0 8px; border-radius: 6px; color: var(--ink2); white-space: nowrap; }
.raw-ni.top { color: var(--ink); font-weight: 600; }
.raw-ni.child { padding-left: 38px; }
.raw-ni.sel { background: var(--selected); color: var(--ink); font-weight: 500; }
.raw-ni .raw-chev { margin-left: auto; color: var(--ink3); }
.raw-side-foot { border-top: 1px solid var(--edge); padding-top: 6px; }
.raw-side-foot .raw-ni { height: 30px; }
.raw-main { display: grid; grid-template-rows: 48px minmax(0, 1fr); min-width: 0; }
.raw-head { display: flex; align-items: center; gap: 12px; padding: 0 14px 0 4px; }
.raw-title { display: flex; flex: 1; align-items: center; gap: 8px; font-weight: 600; }
.raw-title .ic { color: var(--ink2); }
.raw-hsearch { display: flex; align-items: center; gap: 8px; width: 230px; height: 30px; border: 1px solid var(--card-edge); border-radius: 6px; background: var(--page); padding: 0 6px 0 10px; color: var(--ink3); }
.raw-hsearch kbd { margin-left: auto; border: 1px solid var(--card-edge); border-radius: 4px; padding: 0 5px; font: inherit; font-size: 11px; }
.raw-icbtn { position: relative; display: grid; place-items: center; width: 28px; height: 28px; color: var(--ink2); }
.raw-bell b { position: absolute; top: 0; right: 0; display: grid; place-items: center; min-width: 14px; height: 14px; border-radius: 4px; background: #e53935; color: #fff; font-size: 9px; }
.raw-sheet { display: flex; flex-direction: column; gap: 10px; min-height: 0; overflow: hidden; border-top: 1px solid var(--edge); border-left: 1px solid var(--edge); border-top-left-radius: 10px; background: var(--page); padding: 14px 0 0 20px; }
.raw-toolbar { display: flex; gap: 8px; padding-right: 20px; }
.raw-search { display: flex; flex: 1; align-items: center; gap: 8px; height: 32px; border: 1px solid transparent; background: var(--fill); border-radius: 6px; padding: 0 10px; color: var(--ink3); }
.raw-btn { display: inline-flex; align-items: center; gap: 6px; height: 32px; border: 1px solid transparent; border-radius: 6px; background: var(--primary); padding: 0 12px; color: var(--on-primary); font-weight: 500; white-space: nowrap; }
.raw-btn .ic { width: 16px; height: 16px; }
.raw-btn.sec { border-color: var(--card-edge); background: var(--page); color: var(--ink); }
.raw-tabs { display: flex; gap: 20px; border-bottom: 1px solid var(--line2); padding-right: 20px; color: var(--ink3); font-weight: 500; }
.raw-tabs span { display: inline-flex; align-items: center; gap: 5px; padding: 6px 0 8px; white-space: nowrap; }
.raw-tabs i { margin-left: 2px; font-size: 12px; font-style: normal; font-weight: 400; opacity: 0.8; }
.raw-tabs .on { box-shadow: inset 0 -2px 0 var(--ink); color: var(--ink); }
.raw-filters { display: flex; align-items: center; gap: 6px; padding-right: 20px; }
.raw-chip { display: inline-flex; align-items: center; gap: 5px; height: 24px; border: 1px solid var(--card-edge); border-radius: 4px; background: var(--page); padding: 0 8px; color: var(--ink2); font-size: 12px; font-weight: 500; white-space: nowrap; }
/* AppMetricStrip: hairlines above and below, one cell per metric. */
.raw-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-right: 20px; border-top: 1px solid var(--edge); border-bottom: 1px solid var(--edge); }
.raw-metric { display: grid; align-content: start; gap: 5px; min-width: 0; padding: 12px 16px 12px; }
.raw-metric + .raw-metric { border-left: 1px solid var(--edge); }
.raw-metric:first-child { padding-left: 0; }
.raw-m-l { display: flex; align-items: center; gap: 6px; overflow: hidden; color: var(--ink3); font-size: 12px; font-weight: 500; white-space: nowrap; text-overflow: ellipsis; }
.raw-m-v { overflow: hidden; font-size: 24px; font-weight: 600; letter-spacing: -0.3px; line-height: 1.2; white-space: nowrap; }
.raw-m-v small { margin-left: 4px; color: var(--ink3); font-size: 13px; font-weight: 500; letter-spacing: 0; }
.raw-m-c { overflow: hidden; color: var(--ink3); font-size: 12px; white-space: nowrap; text-overflow: ellipsis; }
.raw-m-c.bad { color: var(--red-t); }
.raw-table { min-height: 0; overflow: hidden; }
.raw-table table { width: 100%; min-width: 1000px; border-collapse: collapse; table-layout: fixed; }
.raw-table .w-ret { width: 196px; } .raw-table .w-cli { width: 168px; } .raw-table .w-cyc { width: 196px; } .raw-table .w-amt { width: 112px; }
.raw-table .w-next { width: 130px; } .raw-table .w-col { width: 124px; } .raw-table .w-st { width: 128px; } .raw-table .w-act { width: 44px; }
.raw-table th, .raw-table td { overflow: hidden; border-right: 1px solid var(--line2); border-bottom: 1px solid var(--line2); padding: 0 10px; text-align: left; text-overflow: ellipsis; white-space: nowrap; }
.raw-table th:last-child, .raw-table td:last-child { border-right: 0; }
.raw-table th { height: 36px; border-top: 1px solid var(--line2); color: var(--ink3); font-size: 13px; font-weight: 500; }
.raw-table th .ic { margin: -2px 6px 0 0; color: var(--ink3); }
.raw-table th.more { font-size: 0; }
.raw-table td { height: 52px; }
.raw-table th.r, .raw-table td.r { text-align: right; }
.raw-table td.more { padding: 0; text-align: center; color: var(--ink3); }
.raw-two { display: grid; line-height: 1.3; }
.raw-two b { overflow: hidden; font-weight: 400; text-overflow: ellipsis; }
.raw-two small { overflow: hidden; color: var(--ink3); font-size: 11.5px; text-overflow: ellipsis; }
.raw-two small.warn { color: var(--amber-t); }
.raw-table td:first-child .raw-two b { font-weight: 600; }
.raw-two.end { justify-items: end; }
.raw-two.end b { font-weight: 600; }
.raw-client { display: flex; width: fit-content; align-items: center; gap: 6px; max-width: 100%; height: 20px; overflow: hidden; border-radius: 4px; background: var(--gray-f); padding: 0 6px; color: var(--ink); text-overflow: ellipsis; }
.raw-init { display: grid; flex: none; place-items: center; width: 14px; height: 14px; border-radius: 3px; font-size: 6.5px; font-style: normal; font-weight: 700; line-height: 1; }
.raw-init.red { background: var(--red-f); color: var(--red-t); } .raw-init.blue { background: var(--blue-f); color: var(--blue-t); }
.raw-init.orange { background: var(--orange-f); color: var(--orange-t); } .raw-init.amber { background: var(--amber-f); color: var(--amber-t); }
.raw-cycle { display: grid; gap: 5px; }
.raw-fig { display: flex; align-items: baseline; gap: 6px; overflow: hidden; white-space: nowrap; }
.raw-fig b { flex: none; font-weight: 600; }
.raw-fig small { overflow: hidden; color: var(--ink3); font-size: 11.5px; text-overflow: ellipsis; }
.raw-fig .bad { color: var(--red-t); }
.raw-coll { display: inline-flex; align-items: center; gap: 5px; }
.raw-coll .ic { color: var(--ink3); }
.raw-mark { padding: 1.5px 4px; border-radius: 3px; background: #1f1f1f; color: #fff; font-size: 8.5px; font-style: normal; font-weight: 700; letter-spacing: 0.2px; line-height: 1.2; }
.dark .raw-mark { background: #3a3a3a; }
.raw-tag { display: inline-flex; flex: none; align-items: center; gap: 5px; height: 20px; border-radius: 4px; background: var(--gray-f); padding: 0 6px; color: var(--gray-t); font-size: 13px; font-style: normal; font-weight: 400; white-space: nowrap; }
.raw-tag.green { background: var(--green-f); color: var(--green-t); }
.raw-tag.blue { background: var(--blue-f); color: var(--blue-t); }
.raw-tag.red { background: var(--red-f); color: var(--red-t); }
.raw-tag.amber { background: var(--amber-f); color: var(--amber-t); }
/* RetainerMeter: approved time solid, past the bank red, pending striped; the prepaid meter marks its top-up point. */
.raw-meter { position: relative; display: flex; height: 3px; overflow: visible; border-radius: 2px; background: var(--track); }
.raw-meter > i { display: block; height: 100%; transition: width 0.7s cubic-bezier(0.33, 1, 0.68, 1); }
.raw-meter > i:first-child { border-radius: 2px 0 0 2px; }
.raw-meter .m-used { background: var(--meter); }
.raw-meter .m-over { background: var(--red-t); }
.raw-meter .m-pend { border-radius: 0 2px 2px 0; background: repeating-linear-gradient(135deg, var(--meter) 0 1.2px, transparent 1.2px 3.5px), rgb(10 10 10 / 0.12); }
.dark .raw-meter .m-pend { background: repeating-linear-gradient(135deg, var(--meter) 0 1.2px, transparent 1.2px 3.5px), rgb(250 250 250 / 0.14); }
.raw-meter u { position: absolute; top: -3px; bottom: -3px; width: 2px; background: var(--amber-t); }
.raw-foot { display: flex; align-items: center; gap: 8px; margin-top: auto; border-top: 1px solid var(--line2); padding: 8px 20px 8px 0; color: var(--ink2); }
/* The retainer that moves gets a soft wash each time its cycle changes. */
.raw-table tr.is-focus td { animation: raw-wash 1.4s ease-out; }
.raw--early .raw-table tr.is-focus td, .raw--pending .raw-table tr.is-focus td { animation-name: raw-wash-b; }
@keyframes raw-wash { from { background: var(--selected); } to { background: transparent; } }
@keyframes raw-wash-b { from { background: var(--selected); } to { background: transparent; } }
.raw-table tr.is-focus .raw-tag, .rap-card.is-focus .raw-tag { animation: raw-pop 0.45s cubic-bezier(0.33, 1, 0.68, 1); }
.raw--early .is-focus .raw-tag, .raw--pending .is-focus .raw-tag { animation-name: raw-pop-b; }
@keyframes raw-pop { from { transform: scale(0.85); opacity: 0.4; } to { transform: none; opacity: 1; } }
@keyframes raw-pop-b { from { transform: scale(0.85); opacity: 0.4; } to { transform: none; opacity: 1; } }

/* A frame narrower than the app's 1000px table drops Collection first and trims the columns to fit. */
@container (max-width: 1360px) {
  .raw-table .c-col { display: none; }
  .raw-table .w-ret { width: 180px; } .raw-table .w-cli { width: 168px; } .raw-table .w-cyc { width: 170px; }
  .raw-table table { min-width: 0; }
  /* Compact metric strip so the first rows and their meters stay above the fold. */
  .raw-metric { gap: 3px; padding: 8px 14px; }
  .raw-metric:first-child { padding-left: 0; }
  .raw-m-v { font-size: 20px; }
}
/* Tablet: the sidebar is collapsed, as the app's sidebar toggle leaves it; the table scrolls sideways in the app,
   here it fades out at the edge. Client and Collection are the first columns the window drops. */
@container (max-width: 1080px) {
  .raw-desk { grid-template-columns: minmax(0, 1fr); height: 560px; }
  .raw-side { display: none; }
  .raw-hsearch { display: none; }
  .raw-head { padding-left: 14px; }
  .raw-sheet { padding-left: 16px; }
  .raw-toolbar, .raw-tabs, .raw-filters { padding-right: 16px; }
  .raw-metrics { margin-right: 16px; }
  .raw-table .c-col { display: none; }
  .raw-table table { min-width: 0; }
}
/* A narrow tablet keeps the four metrics on one row (the app folds them to two) so the table stays in view,
   and the table's columns shrink to fit instead of scrolling. */
@container (max-width: 780px) {
  .raw-metric { padding: 8px 12px; }
  .raw-metric:first-child { padding-left: 0; }
  .raw-m-l { font-size: 11px; }
  .raw-m-c { min-height: 2.6em; font-size: 11px; white-space: normal; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  .raw-table .c-cli { display: none; }
  .raw-table th .ic { display: none; }
  .raw-table th { padding: 0 10px; }
  .raw-fig small { display: none; }
  .raw-table .w-ret { width: 190px; } .raw-table .w-cyc { width: 112px; } .raw-table .w-amt { width: 104px; }
  .raw-table .w-next { width: 120px; } .raw-table .w-st { width: 124px; } .raw-table .w-act { width: 34px; }
}

/* ---------- phone (one app point = 1/390 of the width, text at the app's default 125%) ---------- */
.raw-phone { display: none; }
@container (max-width: 600px) {
  .raw-desk { display: none; }
  .raw-phone {
    --u: calc(100cqi / 390);
    --t: calc(var(--u) * 1.25);
    position: relative;
    display: block;
    height: calc(var(--u) * 700);
    overflow: hidden;
    border: 1px solid var(--card-edge);
    border-radius: 10px;
    background: var(--phone-page);
    font-size: calc(var(--t) * 13);
  }
  .raw-phone .ic { width: calc(var(--t) * 16); height: calc(var(--t) * 16); }
  .rap-bar { padding: calc(var(--u) * 14) calc(var(--u) * 16) calc(var(--u) * 8); }
  .rap-bar b { font-size: calc(var(--t) * 17); font-weight: 600; }
  .rap-body { display: grid; gap: calc(var(--u) * 12); padding: 0 calc(var(--u) * 16); }
  .rap-search { display: flex; align-items: center; gap: calc(var(--u) * 8); height: calc(var(--t) * 36); border-radius: calc(var(--u) * 8); background: var(--phone-card); box-shadow: inset 0 0 0 1px var(--phone-line); padding: 0 calc(var(--u) * 12); color: var(--ink3); }
  .rap-seg { display: grid; grid-template-columns: repeat(3, 1fr); gap: calc(var(--u) * 3); border-radius: calc(var(--u) * 8); background: var(--selected); padding: calc(var(--u) * 3); }
  .rap-seg span { display: flex; align-items: center; justify-content: center; gap: calc(var(--u) * 5); height: calc(var(--t) * 28); border-radius: calc(var(--u) * 6); color: var(--ink2); font-weight: 500; }
  .rap-seg i { font-size: calc(var(--t) * 12); font-style: normal; font-weight: 400; opacity: 0.8; }
  .dark .rap-seg { background: rgb(250 250 250 / 0.06); }
  .dark .rap-seg .on { background: #3b3b3b; box-shadow: 0 1px 2px rgb(0 0 0 / 0.3); }
  .rap-seg .on { background: var(--phone-card); box-shadow: 0 1px 2px rgb(0 0 0 / 0.08); color: var(--ink); }
  .rap-card { position: relative; overflow: hidden; border: 1px solid var(--phone-line); border-radius: 14px; background: var(--phone-card); }
  .rap-card::before { content: ""; position: absolute; inset: 0 auto 0 0; width: 4px; background: var(--stripe, var(--gray-t)); transition: background 0.4s ease; }
  .rap-in { padding: calc(var(--u) * 14) calc(var(--u) * 14) calc(var(--u) * 12) calc(var(--u) * 18); }
  .rap-head { display: flex; align-items: flex-start; gap: calc(var(--u) * 8); }
  .rap-head b { flex: 1; min-width: 0; overflow: hidden; font-size: calc(var(--t) * 14); font-weight: 600; line-height: 1.3; text-overflow: ellipsis; white-space: nowrap; }
  .raw-phone .raw-tag { height: calc(var(--t) * 20); font-size: calc(var(--t) * 13); }
  .rap-client { margin: calc(var(--u) * 2) 0 0; overflow: hidden; color: var(--ink3); font-size: calc(var(--t) * 13); text-overflow: ellipsis; white-space: nowrap; }
  .rap-facts { display: flex; flex-wrap: wrap; gap: calc(var(--u) * 8) calc(var(--u) * 18); margin: calc(var(--u) * 12) 0 0; }
  .rap-facts dt { color: var(--ink3); font-size: calc(var(--t) * 11); }
  .rap-facts dd { margin: calc(var(--u) * 2) 0 0; font-size: calc(var(--t) * 13); font-weight: 600; }
  .rap-foot { display: flex; align-items: center; gap: calc(var(--u) * 12); margin-top: calc(var(--u) * 12); border-top: 1px solid var(--phone-line); padding-top: calc(var(--u) * 10); }
  .rap-foot .raw-meter { flex: 1; height: calc(var(--u) * 4); }
  .rap-note { flex: none; color: var(--ink3); font-size: calc(var(--t) * 12); white-space: nowrap; }
  .rap-nav { position: absolute; z-index: 3; right: calc(var(--u) * 12); bottom: calc(var(--u) * 14); left: calc(var(--u) * 12); display: flex; align-items: center; gap: calc(var(--u) * 4); border: 1px solid var(--card-edge); border-radius: 10px; background: var(--phone-card); padding: calc(var(--u) * 6); box-shadow: 0 8px 24px rgb(0 0 0 / 0.12), 0 -14px 18px -6px var(--phone-page); }
  .rap-nav::after { content: ""; position: absolute; right: calc(var(--u) * -13); bottom: calc(var(--u) * -15); left: calc(var(--u) * -13); height: calc(var(--u) * 14); background: var(--phone-page); }
  .rap-nav > span:not(.rap-navmore) { display: grid; flex: 1; justify-items: center; gap: calc(var(--u) * 2); color: var(--ink3); font-size: calc(var(--u) * 10.5); white-space: nowrap; }
  .rap-nav .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
  .rap-navmore { display: grid; flex: none; place-items: center; width: calc(var(--u) * 44); height: calc(var(--u) * 44); border-radius: 8px; background: var(--primary); color: var(--on-primary); }
}
@media (prefers-reduced-motion: reduce) { .raw * { animation: none !important; transition: none !important; } }
</style>
